import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import { Sparkles, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";

/**
 * Personalised / Quote Request page
 * ----------------------------------------------------------------
 * Route: /shop/quote?product=<key>
 *
 * Backing endpoint: POST /api/shop/quote-request (persists to `product_quotes`).
 * Currently supports the `edible_cups` product; adding new personalised
 * products means adding them to `shop_categories.PERSONALISED_PRODUCTS`
 * and (optionally) providing a locale-specific description here.
 *
 * Kept intentionally editorial and consistent with the rest of the site:
 * we use the existing Section shell, card-surface tokens, and gold/black
 * palette — this is not a redesign of the shop.
 */
export default function ShopQuotePage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const productKey = params.get("product") || "edible_cups";

  const [categoryTree, setCategoryTree] = useState(null);
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    company_or_event: "",
    quantity: "",
    personalisation: "",
    logo_branding: "",
    format_details: "",
    delivery_timing: "",
    extra_notes: "",
  });
  const [sending, setSending] = useState(false);
  const [sentId, setSentId] = useState(null);

  useEffect(() => {
    API.get("/shop/categories")
      .then(({ data }) => setCategoryTree(data))
      .catch(() => setCategoryTree(null));
  }, []);

  const product =
    (categoryTree?.personalised || []).find((p) => p.key === productKey) || null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.contact_email) {
      return toast.error(t("common.required_fields", "Please fill all required fields"));
    }
    setSending(true);
    try {
      const { data } = await API.post("/shop/quote-request", {
        product_key: productKey,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
        company_or_event: form.company_or_event || null,
        quantity: form.quantity || null,
        personalisation: form.personalisation || null,
        logo_branding: form.logo_branding || null,
        format_details: form.format_details || null,
        delivery_timing: form.delivery_timing || null,
        extra_notes: form.extra_notes || null,
        language: i18n.language,
      });
      setSentId(data.id);
      toast.success(t("shop.quote_sent", "Thanks — our team will reply within one business day."));
    } catch (err) {
      toast.error(errorMessage(err, t("shop.quote_failed", "Could not submit your quote request.")));
    } finally {
      setSending(false);
    }
  };

  // Success screen
  if (sentId) {
    return (
      <div data-testid="page-shop-quote-success">
        <Section
          eyebrow={t("shop.quote_eyebrow", "— Personalised · Quote request")}
          title={t("shop.quote_sent_title", "Your request is on its way.")}
          subtitle={t(
            "shop.quote_sent_sub",
            "A member of the DONiTSH!T team will reply to your inbox within one business day."
          )}
        >
          <div className="card-surface p-6 md:p-8 max-w-2xl flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 gold-text" />
              <div className="font-display text-xl">
                {t("shop.quote_sent_head", "Request received")}
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              {t(
                "shop.quote_sent_body",
                "We've logged your brief and shared it with our production team. If you don't see a reply, please check your spam folder or contact us via the Support page."
              )}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/shop" className="btn-primary text-sm" data-testid="quote-back-to-shop">
                {t("shop.back_to_shop", "Back to shop")}
              </Link>
              <Link
                to="/support"
                className="btn-secondary text-sm"
                data-testid="quote-support-link"
              >
                {t("shop.quote_contact_support", "Contact Support")}
              </Link>
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] gold-text pt-3">
              {t("shop.quote_reference_label", "Reference")}:{" "}
              <span className="text-white/70">{sentId}</span>
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="page-shop-quote">
      <Section
        eyebrow={t("shop.quote_eyebrow", "— Personalised · Quote request")}
        title={
          product?.name
            ? t("shop.quote_title_for", "Request a quote — {{name}}", { name: product.name })
            : t("shop.quote_title", "Request a personalised quote")
        }
        subtitle={
          product?.summary
            ? product.summary
            : t(
                "shop.quote_subtitle",
                "Made-to-order, bulk and event products are handled individually. Send us a brief and we'll come back with a tailored quote and lead time."
              )
        }
      >
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form column */}
          <form
            onSubmit={submit}
            className="card-surface p-6 md:p-8 lg:col-span-2 grid gap-4"
            data-testid="quote-form"
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                required
                value={form.contact_name}
                onChange={update("contact_name")}
                placeholder={t("shop.quote_field_name", "Your full name")}
                className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
                data-testid="quote-name"
              />
              <input
                required
                type="email"
                value={form.contact_email}
                onChange={update("contact_email")}
                placeholder={t("shop.quote_field_email", "Email address")}
                className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
                data-testid="quote-email"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                value={form.contact_phone}
                onChange={update("contact_phone")}
                placeholder={t("shop.quote_field_phone", "Phone (optional)")}
                className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
                data-testid="quote-phone"
              />
              <input
                value={form.company_or_event}
                onChange={update("company_or_event")}
                placeholder={t("shop.quote_field_company", "Company / event")}
                className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
                data-testid="quote-company"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                value={form.quantity}
                onChange={update("quantity")}
                placeholder={t("shop.quote_field_quantity", "Quantity (e.g. 200, 500, 1000)")}
                className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
                data-testid="quote-quantity"
              />
              <input
                value={form.delivery_timing}
                onChange={update("delivery_timing")}
                placeholder={t(
                  "shop.quote_field_timing",
                  "Delivery timing (e.g. by 15 October)"
                )}
                className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
                data-testid="quote-timing"
              />
            </div>
            <input
              value={form.format_details}
              onChange={update("format_details")}
              placeholder={t(
                "shop.quote_field_format",
                "Format / product details (size, flavour, style)"
              )}
              className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
              data-testid="quote-format"
            />
            <textarea
              value={form.personalisation}
              onChange={update("personalisation")}
              placeholder={t(
                "shop.quote_field_personalisation",
                "Personalisation requirements (colours, message, moulds)"
              )}
              rows={3}
              className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
              data-testid="quote-personalisation"
            />
            <textarea
              value={form.logo_branding}
              onChange={update("logo_branding")}
              placeholder={t(
                "shop.quote_field_logo",
                "Logo / branding (describe, or send by email after submitting)"
              )}
              rows={2}
              className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
              data-testid="quote-logo"
            />
            <textarea
              value={form.extra_notes}
              onChange={update("extra_notes")}
              placeholder={t("shop.quote_field_notes", "Anything else we should know?")}
              rows={3}
              className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm"
              data-testid="quote-notes"
            />

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={sending}
                className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="quote-submit"
              >
                <Send className="w-4 h-4" />
                {sending ? t("common.sending", "Sending\u2026") : t("shop.quote_submit", "Send request")}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn-ghost text-sm"
                data-testid="quote-cancel"
              >
                {t("common.cancel", "Cancel")}
              </button>
            </div>

            <p className="text-xs text-white/50 leading-relaxed pt-3">
              {t(
                "shop.quote_legal_note",
                "By submitting you agree to be contacted about your enquiry. See our Privacy Policy for details."
              )}{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-2 hover:text-white"
                data-testid="quote-privacy-link"
              >
                {t("footer.legal_privacy", "Privacy Policy")}
              </Link>
            </p>
          </form>

          {/* Sidebar column */}
          <aside className="card-surface p-6 md:p-8 flex flex-col gap-4 h-fit">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 gold-text" />
              <div className="text-xs uppercase tracking-[0.28em] gold-text">
                {t("shop.quote_sidebar_eyebrow", "How it works")}
              </div>
            </div>
            <ol className="list-decimal pl-5 text-sm text-white/75 space-y-2 leading-relaxed">
              <li>{t("shop.quote_step_1", "Send us your brief — quantity, format and personalisation.")}</li>
              <li>{t("shop.quote_step_2", "We reply with a tailored quote and production timeline.")}</li>
              <li>{t("shop.quote_step_3", "We produce and ship — DONiTSH!T style, from start to finish.")}</li>
            </ol>
            {product?.long_description && (
              <div className="mt-2 border-t border-white/10 pt-4 text-sm text-white/70 leading-relaxed">
                {product.long_description}
              </div>
            )}
            <div className="mt-2 text-xs text-white/50">
              {t(
                "shop.quote_sidebar_note",
                "Prices vary by quantity, personalisation and lead time."
              )}
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}
